import React, { useState } from 'react';
import {
  Stack,
  TextInput,
  Button,
  Group,
  Switch,
  Select,
  Textarea,
  Alert,
  Divider
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { memberService } from '../../services/memberService';

const MemberForm = ({ member, batches, mode, onSubmit, onCancel }) => {
  const [loading, setLoading] = useState(false);
  console.log('member mode:', member);
  const form = useForm({
    initialValues: {
      full_name: member?.full_name || '',
      phone_number: member?.phone_number || '',
      phone_number_country_code: member?.phone_number_country_code || '+91',
      payment_method: member?.payment_method || 'manual',
      razorpay_payment_id: member?.razorpay_payment_id || '',
      admin_note: member?.admin_note || '',
      assigned_batch: member?.assigned_batch?.toString() || '',
      auto_assign_batch: false,
      has_disabled_wa_notifications: member?.has_disabled_wa_notifications || false,
    },
    validate: {
      full_name: (value) => (!value ? 'Name is required' : null),
      phone_number: (value) => {
        if (!value) return 'Phone number is required';
        if (!/^\d{10}$/.test(value)) return 'Phone number must be 10 digits';
        return null;
      },
      phone_number_country_code: (value) => (!value ? 'Country code is required' : null),
      payment_method: (value) => (!value ? 'Payment method is required' : null),
      razorpay_payment_id: (value, values) => {
        if (values.payment_method === 'Razorpay' && !value) {
          return 'Razorpay Payment ID is required for Razorpay payments';
        }
        return null;
      },
    },
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const submitData = {
        ...values,
        assigned_batch: values.assigned_batch ? parseInt(values.assigned_batch) : null,
      };

      if (mode === 'create') {
        await memberService.createMember(submitData);
        notifications.show({
          title: 'Success',
          message: 'Member created successfully',
          color: 'green',
        });
      } else {
        await memberService.updateMember(member.id, submitData);
        notifications.show({
          title: 'Success',
          message: 'Member updated successfully',
          color: 'green',
        });
      }
      onSubmit();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save member';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const batchOptions = [
    { value: '', label: 'No Batch Assigned' },
    ...batches.map(batch => ({
      value: batch.id.toString(),
      label: `Batch #${batch.batch_number} (${batch.curriculum?.title || 'No Curriculum'})`
    }))
  ];

  const countryCodeOptions = [
    { value: '+91', label: '+91 (India)' },
    { value: '+1', label: '+1 (US/Canada)' },
    { value: '+44', label: '+44 (UK)' },
    { value: '+971', label: '+971 (UAE)' },
    { value: '+353', label: '+353 (Ireland)' },
    { value: '+31', label: '+31 (Netherlands)' },
    { value: '+60', label: '+60 (Malaysia)' },
    { value: '+84', label: '+84 (Vietnam)' },
  ];


  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        {mode === 'edit' && member && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
            Editing member: {member.full_name}
          </Alert>
        )}

        <TextInput
          label="Full Name"
          placeholder="Enter member's full name"
          required
          {...form.getInputProps('full_name')}
        />

        <Group grow>
          <Select
            label="Country Code"
            data={countryCodeOptions}
            required
            {...form.getInputProps('phone_number_country_code')}
          />
          <TextInput
            label="Phone Number"
            placeholder="Enter 10-digit phone number"
            required
            {...form.getInputProps('phone_number')}
          />
        </Group>

        <Divider label="Payment Information" />

        <Select
          label="Payment Method"
          data={[
            { value: 'Razorpay', label: 'Razorpay' },
            { value: 'manual', label: 'Manual Entry' }
          ]}
          required
          {...form.getInputProps('payment_method')}
        />

        {form.values.payment_method === 'Razorpay' && (
          <TextInput
            label="Razorpay Payment ID"
            placeholder="Enter Razorpay payment ID"
            required
            {...form.getInputProps('razorpay_payment_id')}
          />
        )}

        <Divider label="Batch Assignment" />

        {mode === 'create' && (
          <Switch
            label="Auto-assign to batch"
            description="Automatically assign member to an available batch using the assignment algorithm"
            {...form.getInputProps('auto_assign_batch', { type: 'checkbox' })}
          />
        )}

        {!form.values.auto_assign_batch && (
          <Select
            label="Assigned Batch"
            placeholder="Select a batch for this member"
            data={batchOptions}
            {...form.getInputProps('assigned_batch')}
          />
        )}

        <Divider label="Additional Settings" />

        <Textarea
          label="Admin Note"
          placeholder="Internal notes (only visible to admin)"
          rows={3}
          {...form.getInputProps('admin_note')}
        />

        {mode === 'edit' && (
          <Switch
            label="Disable WhatsApp Notifications"
            description="Check to disable WhatsApp notifications for this member"
            {...form.getInputProps('has_disabled_wa_notifications', { type: 'checkbox' })}
          />
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            {mode === 'create' ? 'Create Member' : 'Update Member'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default MemberForm;